/**
 * The two sides `wayai knowledge migrate` talks to, over the CLI's one login: WayAI's setup and
 * file routes for the knowledge it reads, and the `/api/data` proxy for the knowledge base it
 * writes — and, for `wayai knowledge refresh-preview`, the preview file snapshot it pages; for
 * `wayai knowledge cut-over`, a preview hub's setup routes and the toolsets it writes.
 *
 * Every request to a route with a per-person budget is paced below it — the budget the person's
 * other commands and web console share — and waits out a 429 when that budget is spent: the data
 * surface at the rate `wayai files push` keeps, the setup routes and `POST /api/files/sign` each at
 * four fifths of theirs. A signed URL's bytes are fetched without the session, from no budgeted route.
 */

import { getEndpointConfig } from '@wayai/core';
import { DATA_PROXY_PREFIX } from '@wayai/core/contracts';
import { ApiError, type ApiClient } from '../lib/api-client.js';
import { expected } from '../lib/expected.js';
import { Pacer, type PacerOptions } from '../communities/pacer.js';
import type { DataClient } from '../data/client.js';
import { getBaseRecord } from '../data/config-as-code/api.js';
import { ENTITY_KINDS } from '../data/config-as-code/types.js';
import { fileWriteHeaders } from '../data/files-sync/headers.js';
import { pathSegment, pathSegments } from '../data/helpers.js';
import type { BaseRow, HubListing, KnowledgeBase, KnowledgeReader, RemoteFile, RemoteFileType } from './migrate.js';
import type { PreviewKnowledgeBase } from './refresh-preview.js';
import type { CutOverResource, HubConnection, HubSetup, RemoteToolset, ToolsetKnowledgeBase } from './cut-over.js';
import { sanitizeTerminalText } from '../lib/terminal-output.js';
import type { ConnectionItemResponse } from '@wayai/core/contracts';
import { rawTokenFrom } from '../data/commands/tokens.js';
import { TOOLSET_SLUG_SETTING } from '@wayai/core/contracts/rekor-toolset';
import type { DesiredFile, FileTypeSpec } from './plan.js';

/** Rows per file listing request: the most the listing returns. */
const LIST_PAGE = 1000;
/** Listing pages read before refusing: a million files, or a server answering in circles. */
const MAX_LIST_PAGES = 1000;

/** The label every version this command writes carries, so a file's history says where it came from. */
export const MIGRATION_LABEL = 'wayai knowledge migrate';

/** Four fifths of the per-person budget of the routes `path` falls under, as `wayai files push` paces its own. */
export function pacedRequestsPerMinute(path: string, fallback: number): number {
  const budget = getEndpointConfig(path)?.userLimit;
  return budget ? Math.max(1, Math.floor((budget * 4) / 5)) : fallback;
}

/** A pacer for one budget, saying why it waits. */
function pacerFor(path: string, what: string): Pacer {
  const perMinute = pacedRequestsPerMinute(path, 60);
  const onWait: PacerOptions['onWait'] = (ms, reason) =>
    console.error(
      reason === 'paced'
        ? `  Pacing ${what} requests at ${perMinute} a minute: waiting ${Math.ceil(ms / 1000)} s.`
        : `  The ${what} request budget is spent (your other commands and the web console share it): waiting ${Math.ceil(ms / 1000)} s.`,
    );
  return new Pacer({ perMinute, onWait });
}

/** The hubs the caller can see in `orgId`, production and previews, from the setup schema. */
export function hubsOf(schema: Awaited<ReturnType<ApiClient['getUserSchema']>>, orgId: string): HubListing[] {
  return (schema.data?.flat?.hubsByOrg?.[orgId] ?? []).map((hub) => ({
    hub_id: hub.id,
    hub_name: hub.name,
    hub_environment: typeof hub.hub_environment === 'string' ? hub.hub_environment : '',
    production_hub_id: typeof hub.production_hub_id === 'string' && hub.production_hub_id ? hub.production_hub_id : null,
  }));
}

/** The routes whose per-person budgets the reader paces itself below: one pacer each. */
export const READER_BUDGETS = { setup: '/api/setup/organization-resources', sign: '/api/files/sign' } as const;

/** The reader's pacers: the setup routes', and `POST /api/files/sign`'s, which is a budget of its own. */
export interface ReaderPacers {
  setup: Pick<Pacer, 'run'>;
  sign: Pick<Pacer, 'run'>;
}

/** WayAI's knowledge resources, read through the setup routes. */
export function knowledgeReader(
  api: ApiClient,
  orgId: string,
  pacers: ReaderPacers = { setup: pacerFor(READER_BUDGETS.setup, 'setup'), sign: pacerFor(READER_BUDGETS.sign, 'file signing') },
): KnowledgeReader {
  const { setup, sign } = pacers;
  const run = <T>(send: () => Promise<T>) => setup.run(send);
  return {
    listOrgResources: async () => (await run(() => api.listOrgResources(orgId))).data ?? [],
    listOrgResourceFiles: async (id) => (await run(() => api.listOrgResourceFiles(orgId, id))).data ?? [],
    listOrgResourceFolders: async (id) => (await run(() => api.listOrgResourceFolders(orgId, id))).data ?? [],
    listHubs: async () => hubsOf(await run(() => api.getUserSchema()), orgId),
    listHubResources: async (hubId) => (await run(() => api.listHubResources(hubId))).data ?? [],
    listHubResourceFiles: async (hubId, id) => (await run(() => api.listHubResourceFiles(hubId, id))).data ?? [],
    listHubResourceFolders: async (hubId, id) => (await run(() => api.listHubResourceFolders(hubId, id))).data ?? [],
    readContent: async (ref) => {
      const url =
        ref.kind === 'org'
          ? (await run(() => api.getOrgResourceFileContent(orgId, ref.orgFileId))).data.signed_url
          : (await sign.run(() => api.signFileUrl(ref.bucket, ref.objectKey))).signedUrl;
      return api.downloadSigned(url);
    },
  };
}

/** `read`'s answer, or null when the base answers 404. */
async function unlessMissing<T>(read: () => Promise<T>): Promise<T | null> {
  try {
    return await read();
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** The knowledge base, through the data proxy. */
export function knowledgeBase(client: DataClient): KnowledgeBase & PreviewKnowledgeBase & ToolsetKnowledgeBase {
  const pacer = pacerFor(`${DATA_PROXY_PREFIX}/files`, 'data');
  const request = <T>(method: string, path: string, body?: unknown) => pacer.run(() => client.request<T>(method, path, body));
  const baseSeg = (id: string) => pathSegment(id, '--base');
  const typeSeg = (id: string) => pathSegment(id, 'file type');
  const toolsetSeg = (id: string) => pathSegment(id, 'toolset slug');
  /**
   * The promotion of `ids` in one config section alone. Every other section — file types and
   * toolsets included — is named EMPTY: a section left out is promoted whole, which would ship
   * whatever else the preview holds. (Credentials are not a section: like every promotion, this one
   * publishes the ones the preview opted in to production.)
   */
  const promoteOnly = (production: string, preview: string, section: 'file_types' | 'toolsets', ids: string[], dryRun: boolean) =>
    request('POST', `/v1/${baseSeg(production)}/promote`, {
      source_base_id: preview,
      dry_run: dryRun,
      ...Object.fromEntries(ENTITY_KINDS.map((kind) => [kind, []])),
      file_types: [],
      [section]: ids,
    });

  return {
    getBase: (id) => unlessMissing(() => pacer.run(() => getBaseRecord(client, id) as Promise<BaseRow>)),
    listPreviews: (id) =>
      pacer.run(() =>
        client.collectPages<BaseRow>((cursor) => `/v1/${baseSeg(id)}/previews${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`),
      ),
    listFileTypes: async (base) => (await request<RemoteFileType[]>('GET', `/v1/${baseSeg(base)}/file-types`)) ?? [],
    getFileType: (base, id) => unlessMissing(() => request<RemoteFileType>('GET', `/v1/${baseSeg(base)}/file-types/${typeSeg(id)}`)),
    async putFileType(base, spec: FileTypeSpec) {
      const { id, ...body } = spec;
      await request('PUT', `/v1/${baseSeg(base)}/file-types/${typeSeg(id)}`, body);
    },
    promoteFileTypes: (production, preview, ids, dryRun) => promoteOnly(production, preview, 'file_types', ids, dryRun),
    async listFiles(base, fileType) {
      const rows: RemoteFile[] = [];
      for (let page = 0; page < MAX_LIST_PAGES; page++) {
        const qs = new URLSearchParams({ limit: String(LIST_PAGE), offset: String(page * LIST_PAGE) });
        const pageRows = (await request<RemoteFile[]>('GET', `/v1/${baseSeg(base)}/files/${typeSeg(fileType)}?${qs}`)) ?? [];
        for (const row of pageRows) rows.push(row);
        if (pageRows.length < LIST_PAGE) return rows;
      }
      throw expected(`The listing of file type "${fileType}" on base "${base}" did not end after ${MAX_LIST_PAGES} pages.`);
    },
    getToolset: (base, slug) => unlessMissing(() => request<RemoteToolset>('GET', `/v1/${baseSeg(base)}/toolsets/${toolsetSeg(slug)}`)),
    async putToolset(base, spec) {
      const { id, ...body } = spec;
      await request('PUT', `/v1/${baseSeg(base)}/toolsets/${toolsetSeg(id)}`, { ...body, actions: [] });
    },
    promoteToolsets: (production, preview, slugs, dryRun) => promoteOnly(production, preview, 'toolsets', slugs, dryRun),
    mintToolsetToken: async (base, slug, name) =>
      rawTokenFrom(await request('POST', '/v1/tokens', { name, toolset_binding: { base_id: base, toolset_id: slug } })),
    // `requestRaw`: the page is read whole, `{ data: … }`, as WayAI's copy of its answer declares it
    // (`@wayai/core/contracts` `rekorFileSnapshotAnswerSchema`).
    snapshotPage: (preview, cursor) =>
      pacer.run(() => client.requestRaw('POST', `/v1/${baseSeg(preview)}/files/snapshot`, cursor === undefined ? {} : { cursor })),
    async putFile(base, file: DesiredFile, bytes: Uint8Array) {
      await pacer.run(() =>
        client.upload(
          `/v1/${baseSeg(base)}/files/${typeSeg(file.fileType)}/${pathSegments(file.path)}`,
          bytes,
          file.contentType,
          fileWriteHeaders({ label: MIGRATION_LABEL, metadata: file.metadata }),
        ),
      );
    },
  };
}

/** The routes whose per-person budgets a cut-over paces itself below: one pacer each. */
export const CUT_OVER_BUDGETS = { setup: '/api/setup/agents', automations: '/api/automations' } as const;

/** The hub's connections that have an id, as a cut-over reads them. */
function connectionsOf(rows: ConnectionItemResponse[] | undefined): HubConnection[] {
  return (rows ?? []).flatMap((c) =>
    c.connection_id
      ? [{ connection_id: c.connection_id, connection_display_name: c.connection_display_name, connector_id: c.connector_id, settings: c.settings }]
      : [],
  );
}

/** A preview hub's setup routes, for `wayai knowledge cut-over`. */
export function hubSetup(
  api: ApiClient,
  orgId: string,
  pacers: { setup: Pick<Pacer, 'run'>; automations: Pick<Pacer, 'run'> } = {
    setup: pacerFor(CUT_OVER_BUDGETS.setup, 'setup'),
    automations: pacerFor(CUT_OVER_BUDGETS.automations, 'automations'),
  },
): HubSetup {
  const run = <T>(send: () => Promise<T>) => pacers.setup.run(send);
  const auto = <T>(send: () => Promise<T>) => pacers.automations.run(send);
  return {
    listHubs: async () => hubsOf(await run(() => api.getUserSchema()), orgId),
    hubDiff: async (hubId) => (await run(() => api.getHubDiff(hubId))).data,
    // The rows are the hub's `resource` rows whole, the move's mark (`moved_file_type`) among them.
    listResources: async (hubId) => ((await run(() => api.listHubResources(hubId))).data ?? []) as CutOverResource[],
    listAgents: async (hubId) => (await run(() => api.listAgents(hubId))).data ?? [],
    listAgentLinks: async (hubId, agentId) => (await run(() => api.listAgentResources(hubId, agentId))).resources ?? [],
    listAgentTools: async (hubId, agentId) => {
      const tools = await run(() => api.listAgentTools(hubId, agentId));
      return { native: tools.tools ?? [], connection: (tools.mcp_tools ?? []).map((t) => ({ ...t, tool_id: t.tool_id ?? null })) };
    },
    listConnections: async (hubId) => connectionsOf((await run(() => api.listConnections(hubId))).connections),
    listAutomations: async (hubId) => (await auto(() => api.listAutomations(hubId))).automations ?? [],
    // The answer lists the hub's connections, the new one among them.
    addToolsetConnection: async (hubId, input) =>
      connectionsOf(
        (
          await run(() =>
            api.addConnection({
              hub_id: hubId,
              connector_id: input.connectorId,
              connection_display_name: input.name,
              settings: { [TOOLSET_SLUG_SETTING]: input.toolset },
              access_token: input.token,
            }),
          )
        ).connections,
      ),
    async syncConnection(hubId, connectionId) {
      const answer = await run(() => api.syncMcp(hubId, connectionId));
      if (!answer.success) {
        throw expected(`Syncing connection ${connectionId}'s tools failed: ${sanitizeTerminalText(answer.message ?? 'no reason given')}`);
      }
    },
    async addNativeTool(hubId, agentId, toolNativeId) {
      await run(() => api.addNativeTool(hubId, agentId, toolNativeId));
    },
    async addConnectionTool(hubId, agentId, mcpToolId) {
      await run(() => api.addMcpTool(hubId, agentId, mcpToolId));
    },
    async enableTool(hubId, agentId, toolId) {
      await run(() => api.enableTool(hubId, agentId, toolId));
    },
    async updateAgentText(hubId, agentId, patch) {
      await run(() => api.updateAgent(hubId, agentId, patch));
    },
    async updateAutomationAction(hubId, automationId, action) {
      await auto(() => api.updateAutomation(hubId, automationId, { action }));
    },
    async disableLink(hubId, agentId, resourceId) {
      await run(() => api.updateAgentResource({ hub_id: hubId, agent_id: agentId, resource_id: resourceId, enabled: false }));
    },
    recordMove: async (body) => (await run(() => api.recordKnowledgeMove(body))).data.recorded,
  };
}
